import { useState, useEffect, useCallback } from 'react';
import { sessionsV2Api, SessionV2, SessionsV2Filters } from '../../../api/sessionsV2Api';
import { sitesV2Api } from '../../../api/sitesV2Api';

interface UseAllSitesShiftsOptions {
  filters?: SessionsV2Filters;
  enabled?: boolean;
}

export const useAllSitesShifts = ({
  filters,
  enabled = true,
}: UseAllSitesShiftsOptions) => {
  const [sessions, setSessions] = useState<SessionV2[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    if (!enabled) return;

    try {
      setLoading(true);
      setError(null);

      const sites = await sitesV2Api.getMySites();
      const effectiveFilters: SessionsV2Filters = filters
        ? { ...filters }
        : { limit: 30 };

      if (!filters?.startDate && !filters?.endDate) {
        effectiveFilters.limit = 30;
      }

      const allSessions: SessionV2[] = [];
      const results = await Promise.allSettled(
        sites.map((site) => sessionsV2Api.getSiteSessions(site.id, effectiveFilters))
      );

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          allSessions.push(...result.value);
        }
      });

      allSessions.sort(
        (a, b) =>
          new Date(b.timeStart).getTime() - new Date(a.timeStart).getTime()
      );

      setSessions(allSessions);
    } catch (err: any) {
      console.error('Error loading all sites sessions:', err);
      setError(err.response?.data || err.message || 'Ошибка загрузки смен');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, filters]);

  useEffect(() => {
    if (enabled) {
      loadSessions();
    } else {
      setSessions([]);
      setError(null);
    }
  }, [enabled, loadSessions]);

  const statistics = {
    totalSessions: sessions.length,
    totalHours: sessions.reduce((sum, s) => sum + (s.hours || 0), 0),
    totalAmount: sessions.reduce((sum, s) => sum + (s.totalAmount || 0), 0),
    paidAmount: sessions.reduce((sum, s) => sum + (s.paidAmount || 0), 0),
    bonusPaidAmount: sessions.reduce(
      (sum, s) => sum + (s.bonusPaidAmount || 0),
      0
    ),
    remainingAmount: sessions.reduce(
      (sum, s) => sum + (s.remainingAmount || 0),
      0
    ),
    hasFinancialAccess: sessions.some((s) => s.hasFinancialAccess),
    uniqueUsers: new Set(sessions.map((s) => s.userId)).size,
    uniqueSites: new Set(sessions.map((s) => s.siteId)).size,
  };

  return {
    sessions,
    loading,
    error,
    statistics,
    refresh: loadSessions,
  };
};
